import React, { createRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditionFormContext } from '../Components/EditionFormWrapper';
import { STEPS } from '../types';
import { useFormContext, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Box, TextField, Select, MenuItem, FormControl, InputLabel, Stack, Typography, useTheme, IconButton, CircularProgress } from '@mui/material';
import ButtonModal from 'packages/ui/Components/Button';
import { ParentSize } from '@visx/responsive';
import ContentFileField from './ContentFileField';
import { getFileNameFromUrl } from '../../../helpers';

interface DocumentContentStepProps {
  isLoading: boolean;
}

const DocumentContentStep: React.FC<DocumentContentStepProps> = ({ isLoading }) => {
  const { t } = useTranslation();
  const { setActiveStep } = useEditionFormContext();
  const navigate = useNavigate();
  const theme = useTheme();

  const { control, watch, setValue, formState: { errors }, trigger } = useFormContext();

  const uploadedFile = watch('file');
  const dropzoneRef = createRef<{ open: () => void }>();

  const handleNext = async (): Promise<void> => {
    const isValid = await trigger(['name', 'type', 'file']);
    if (isValid) {
      setActiveStep(STEPS.SITES);
    }
  };

  const handleFileChange = (file: File | null): void => {
    setValue('file', file, { shouldValidate: true });
  };

  const customInputStyle = {
    background: 'white',
    '& .MuiOutlinedInput-root': {
      borderRadius: '6px',
      '& fieldset': {
        borderColor: theme.palette.grey['light'],
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
  };

  const getFileIcon = (fileName: string): JSX.Element | null => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    if (extension === 'pdf') {
      return <i className="fa-regular fa-file-pdf" style={{ fontSize: '45px', color: '#DCDCDC' }} />;
    } else if (['xls', 'xlsx'].includes(extension || '')) {
      return <i className="fa-regular fa-file-xls" style={{ fontSize: '45px', color: '#DCDCDC' }} />;
    }
    return null;
  };

  const handleRemoveFile = (): void => {
    setValue('file', null, { shouldValidate: true });
  };

  const renderFileDisplay = () => {
    if (isLoading) {
      return (
        <Box sx={{
          width: '100%',
          height: '250px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px dashed #ccc',
          borderRadius: '4px',
        }}>
          <CircularProgress />
        </Box>
      );
    }

    if (typeof uploadedFile === 'string') {
      const fileName = getFileNameFromUrl(uploadedFile);
      return (
        <Box sx={{
          width: '100%',
          height: '250px',
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px dashed #ccc',
          borderRadius: '4px',
          padding: '16px',
          position: 'relative',
        }}>
          {getFileIcon(fileName)}
          <Typography>{fileName}</Typography>
          <IconButton
            onClick={handleRemoveFile}
            sx={{
              fontSize: '13px',
              position: 'absolute',
              top: '10px',
              right: '10px',
              padding: '6px 8px',
              borderRadius: '50%',
              backgroundColor: '#DCDCDC',
              color: 'white',
              '&:hover': {
                backgroundColor: '#DADADA',
              },
            }}
          >
            <i className="fa-solid fa-xmark" />
          </IconButton>
        </Box>
      );
    } else if (uploadedFile instanceof File) {
      return (
        <Box sx={{
          width: '100%',
          height: '250px',
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px dashed #ccc',
          borderRadius: '4px',
          padding: '16px',
          position: 'relative',
        }}>
          {getFileIcon(uploadedFile.name)}
          <Typography>{uploadedFile.name}</Typography>
          <IconButton
            onClick={handleRemoveFile}
            sx={{
              fontSize: '13px',
              position: 'absolute',
              top: '10px',
              right: '10px',
              padding: '6px 8px',
              borderRadius: '50%',
              backgroundColor: '#DCDCDC',
              color: 'white',
              '&:hover': {
                backgroundColor: '#DADADA',
              },
            }}
          >
            <i className="fa-solid fa-xmark" />
          </IconButton>
        </Box>
      );
    } else {
      return (
        <ContentFileField
          ref={dropzoneRef}
          name="file"
          onFileChange={handleFileChange}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment     
          // @ts-ignore
          control={control}
        />
      );
    }
  };

  return (
    <Stack alignItems="center" justifyContent="space-between" height="100%" gap={2}>
      <ParentSize>
        {() => (
          <Box sx={{ height: '100%', overflowY: 'auto', width: '100%', paddingTop: 1 }}>
            <Stack spacing={2} width="100%">
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <Box position="relative">
                    <TextField
                      {...field}
                      label={t('documents.edition.content.name')}
                      error={!!errors.name}
                      helperText={errors.name?.message as string || ''}
                      InputProps={{
                        startAdornment: (
                          <i className="fa-regular fa-input-text" style={{ marginRight: '8px', color: theme.palette.primary.main }} />
                        ),
                      }}
                      InputLabelProps={{
                        sx: { left: field.value ? 0 : 30, top: field.value ? 0 : -10 },
                        shrink: !!field.value
                      }}
                      variant="outlined"
                      fullWidth
                      sx={customInputStyle}
                    />
                    {isLoading && (
                      <CircularProgress size={24} sx={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        marginTop: '-12px',
                        color: theme.palette.primary.main,
                      }} />
                    )}
                  </Box>
                )}
              />
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <Box position="relative">
                    <FormControl fullWidth variant="outlined" sx={customInputStyle}>
                      <InputLabel
                        sx={{ left: field.value ? 0 : 20, top: field.value ? 0 : -10 }}
                        shrink={!!field.value}
                      >
                        {t('documents.edition.content.type')}
                      </InputLabel>
                      <Select
                        {...field}
                        error={!!errors.type}
                        startAdornment={
                          <i className="fa-regular fa-file" style={{ marginRight: '8px', color: theme.palette.primary.main }} />
                        }
                        label={t('documents.edition.content.type')}
                        notched={!!field.value}
                      >
                        <MenuItem value="financial_report" sx={{ fontSize: '12px' }}>
                          {t('documents.edition.types.financial_report')}
                        </MenuItem>
                        <MenuItem value="indicator_report" sx={{ fontSize: '12px' }}>
                          {t('documents.edition.types.indicator_report')}
                        </MenuItem>
                        <MenuItem value="support_document" sx={{ fontSize: '12px' }}>
                          {t('documents.edition.types.support_material')}
                        </MenuItem>
                        <MenuItem value="other" sx={{ fontSize: '12px' }}>
                          {t('documents.edition.types.others')}
                        </MenuItem>
                      </Select>
                      {errors.type && <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5, ml: 2 }}>{errors.type.message as string}</Box>}
                    </FormControl>
                    {isLoading && (
                      <CircularProgress size={24} sx={{
                        position: 'absolute',
                        top: '50%',
                        right: 8,
                        marginTop: '-12px',
                        color: theme.palette.primary.main,
                      }} />
                    )}
                  </Box>
                )}
              />
              <Box flex={1} width={'100%'} height={'100%'} minHeight="200px">
                {renderFileDisplay()}
              </Box>
            </Stack>
          </Box>
        )}
      </ParentSize>
      <Stack direction="row" justifyContent="space-between" width="100%" spacing={2}>
        <ButtonModal
          variantType={'cancel'}
          onClick={() => navigate({ pathname: '..' })}
          sx={{ height: '44px' }}
        >
          {t('documents.edition.buttons.cancel')}
        </ButtonModal>
        <ButtonModal
          variantType={'next'}
          onClick={handleNext}
          disabled={isLoading || Object.keys(errors).length > 0}
          sx={{ height: '44px' }}
        >
          {t('documents.edition.buttons.next')}
        </ButtonModal>
      </Stack>
    </Stack>
  );
};

export default DocumentContentStep;
